import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DISPLAYABLE_CHANNELS, DISPLAYED_CHANNELS } from '@/constants/messageChannels';
import AppContext from '@/contexts/appContext';
import { isSoftPhoneActive, useSoftPhone } from '@/contexts/softPhoneContext';
import { useDiscardConfirm } from '@/hooks/useDiscardConfirm';
import useInfiniteScrollSentinel from '@/hooks/useInfiniteScrollSentinel';
import { usePersonMessages } from '@/hooks/usePersonMessages';
import PersonContext from '@/pages/Person/PersonContext';
import PhoneNumber from '@/pages/Profile/components/ContactInfoSection/components/PhoneNumberSection/PhoneNumber';
import MessageRepository from '@/repositories/MessageRepository';
import { Message, PendingMessage } from '@/types/message';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import formatPersonName from '@/utils/formatPersonName';
import { htmlToMarkdown } from '@/utils/htmlToMarkdown';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import usFormatPhoneNumber from '@/utils/usFormatPhoneNumber';

import ChannelSelect from './components/ChannelSelect';
import EmailComposer from './components/EmailComposer';
import EmailMessage from './components/EmailMessage';
import MessageFilterBar from './components/MessageFilterBar';
import SmsComposer from './components/SmsComposer';
import MessageBubble from './components/TextMessage';
import { MessageFilters } from './components/types';
import VoiceMessage from './components/VoiceMessage';
import { useMessageSenders } from './hooks/useMessageSenders';
import { Channel, ChannelOption, ChannelId } from './types';

const PersonMessaging = () => {
  const { person } = useContext(PersonContext);
  const { addMessage } = useContext(AppContext);
  const softPhone = useSoftPhone();
  const messageRepository = MessageRepository();
  const { confirmDiscard } = useDiscardConfirm('You have an unsaved message. Discard it?');

  const handleCall = useCallback(
    (number: string) => {
      if (!isSoftPhoneActive(softPhone)) return;
      softPhone.startCall(number, person.id).catch((err: unknown) => {
        const message = err instanceof Error ? err.message : '';
        addMessage({
          level: 'error',
          text:
            message || `Failed to connect call to ${usFormatPhoneNumber(number)}. Please try again.`
        });
      });
    },
    [softPhone, person.id, addMessage]
  );

  const hasPhone = Boolean(person.default_phone);
  const hasEmail = Boolean(person.default_email);
  const hasSmsEnabled = person.settings?.channel_sms_enabled ?? true;

  const { data: channelOptions = [] } = useMessageSenders({ hasPhone, hasEmail });

  const [searchParams, setSearchParams] = useSearchParams();
  const senderChannelParam = searchParams.get('channel') as ChannelId | null;
  const rawFilterChannelParam = searchParams.get('filterChannel');
  const filterChannelParam = DISPLAYABLE_CHANNELS.includes(rawFilterChannelParam as Channel)
    ? (rawFilterChannelParam as Channel)
    : null;
  const targetMessageId = searchParams.get('messageId');
  const targetMessageRef = useRef<HTMLLIElement>(null);
  const [hasScrolledToTarget, setHasScrolledToTarget] = useState(false);

  const defaultOption = channelOptions[0];
  const initialOption = senderChannelParam
    ? (channelOptions.find((o) => o.id === senderChannelParam) ?? defaultOption)
    : defaultOption;

  const [selectedValue, setSelectedValue] = useState(initialOption?.value ?? 'sms-0');
  const selectedOption = channelOptions.find((o) => o.value === selectedValue) ?? defaultOption;
  const [draftMessage, setDraftMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isEmailComposerDirty, setIsEmailComposerDirty] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [emailComposerKey, setEmailComposerKey] = useState(0);
  const [messageFilters, setMessageFilters] = useState<MessageFilters>({
    channel: filterChannelParam ?? undefined,
    created_by_id: undefined,
    showAutoMessages: false
  });

  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);

  const generateId = (): string =>
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const handleChannelUpdate = useCallback((updatedChannel: string) => {
    setPendingMessages((prev) => prev.filter((m) => m.channel !== updatedChannel));
  }, []);

  const {
    data,
    isPending: isPendingMessages,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = usePersonMessages(
    person.id,
    messageFilters.channel as Channel | undefined,
    handleChannelUpdate,
    {
      ...(messageFilters.showAutoMessages ? {} : { auto: 'false' }),
      ...(messageFilters.channel ? {} : { channel__in: DISPLAYED_CHANNELS }),
      ...(messageFilters.created_by_id ? { created_by_id: messageFilters.created_by_id } : {})
    }
  );

  // Newest first, ordered by the viewset's queryset rather than anything asked
  // for here.
  const messages = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data]);

  const sentinelRef = useInfiniteScrollSentinel(hasNextPage, isFetchingNextPage, fetchNextPage);

  const targetMessageFound = targetMessageId
    ? messages.some((message) => message.id === targetMessageId)
    : true;

  // A linked message may be older than what's loaded on the first page, so keep
  // paginating until it shows up or there's nothing left to fetch.
  useEffect(() => {
    if (!targetMessageId || targetMessageFound || isPendingMessages) return;
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [
    targetMessageId,
    targetMessageFound,
    isPendingMessages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  ]);

  useEffect(() => {
    setHasScrolledToTarget(false);
  }, [targetMessageId]);

  useEffect(() => {
    if (!targetMessageId || hasScrolledToTarget || !targetMessageFound) return;
    if (!targetMessageRef.current) return;
    targetMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHasScrolledToTarget(true);
  }, [targetMessageId, targetMessageFound, hasScrolledToTarget]);

  const hasDraft = Boolean(draftMessage || emailSubject || isEmailComposerDirty);

  const handleChannelChange = (option: ChannelOption) => {
    if (hasDraft && !confirmDiscard()) return;
    setSelectedValue(option.value);
    setMessageFilters((prev) => ({ ...prev, channel: option.id }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('channel', option.id);
      return next;
    });
    setDraftMessage('');
    setEmailSubject('');
    setIsEmailComposerDirty(false);
    setReplyToMessage(null);
  };

  const handleFiltersChange = (newFilters: MessageFilters) => {
    setMessageFilters(newFilters);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newFilters.channel) {
        next.set('filterChannel', newFilters.channel);
      } else {
        next.delete('filterChannel');
      }
      return next;
    });
  };

  const handleReply = (message: Message) => {
    setReplyToMessage(message);
    const ourNumber = message.direction === 'out' ? message.sender : message.recipient;
    const matchingOption =
      channelOptions.find((o) => o.id === message.channel && o.sender === ourNumber) ??
      channelOptions.find((o) => o.id === message.channel);
    if (matchingOption) setSelectedValue(matchingOption.value);
    if (message.channel === Channel.EMAIL) {
      const original = message.subject ?? '';
      const subject = original.startsWith('Re:') ? original : `Re: ${original}`.trimEnd();
      setEmailSubject(subject);
    }
  };

  const handleAttachmentDownload = (attachmentId: string, kustomerMessageId: string) =>
    messageRepository.getAttachmentDownloadUrl(attachmentId, kustomerMessageId, person.id);

  const handleDismissEmailReply = () => {
    setReplyToMessage(null);
    setEmailSubject('');
  };

  const handleDiscard = () => {
    setDraftMessage('');
    setEmailSubject('');
    setReplyToMessage(null);
  };

  const sendSms = useMutation({
    mutationFn: () =>
      messageRepository.send({
        personId: person.id,
        content: draftMessage,
        channel: Channel.SMS,
        replyToMessageId: replyToMessage?.id,
        sender: selectedOption?.sender
      }),
    onMutate: () => {
      const id = 'pending-' + generateId();
      setPendingMessages((prev) => [
        { id, preview: draftMessage, channel: Channel.SMS, subject: null },
        ...prev
      ]);
      return { id };
    },
    onSuccess: () => {
      addMessage({
        level: 'success',
        text: `Sent SMS to ${formatPersonName({ person })} at ${usFormatPhoneNumber(person.default_phone)}`
      });
      setDraftMessage('');
      setReplyToMessage(null);
    },
    onError: (error: AxiosError, _vars, context) => {
      if (context?.id) setPendingMessages((prev) => prev.filter((m) => m.id !== context.id));
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const sendEmail = useMutation({
    mutationFn: ({
      draftId,
      attachments
    }: {
      draftId: string | null;
      attachments: { id: string; name: string; size: number }[];
    }) =>
      messageRepository.sendEmail({
        personId: person.id,
        subject: emailSubject,
        content: htmlToMarkdown(draftMessage),
        sender: selectedOption?.sender,
        replyToMessageId: replyToMessage?.id,
        draftId,
        attachments
      }),
    onMutate: () => {
      const id = 'pending-' + generateId();
      setPendingMessages((prev) => [
        {
          id,
          preview: htmlToMarkdown(draftMessage),
          channel: Channel.EMAIL,
          subject: emailSubject
        },
        ...prev
      ]);
      return { id };
    },
    onSuccess: () => {
      addMessage({
        level: 'success',
        text: `Sent email to ${formatPersonName({ person })} at ${person.default_email}`
      });
      setDraftMessage('');
      setEmailSubject('');
      setReplyToMessage(null);
      setEmailComposerKey((k) => k + 1);
    },
    onError: (error: AxiosError, _vars, context) => {
      if (context?.id) setPendingMessages((prev) => prev.filter((m) => m.id !== context.id));
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  return (
    <Container type="page">
      <div className="flex flex-col w-full">
        {!hasPhone && !hasEmail ? (
          <div className="flex flex-1 items-center justify-center p-12">
            <div className="text-center">
              <Heading
                text={`No phone or email found for ${formatPersonName({ person })}`}
                value={3}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="flex-shrink-0 bg-gray-100 pt-2 pb-2">
              <p className="text-sm font-bold mb-2">Send a new message</p>

              {/* Sender / Template row */}
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <ChannelSelect
                  channelOptions={channelOptions}
                  selectedOption={selectedOption}
                  onChange={handleChannelChange}
                />
              </div>

              {/* Composer body */}
              {selectedOption?.id === Channel.SMS ? (
                <SmsComposer
                  draftMessage={draftMessage}
                  isLoading={sendSms.isPending}
                  placeholder={`Send to ${usFormatPhoneNumber(person.default_phone ?? '')}`}
                  replyToMessage={replyToMessage}
                  smsDisabled={!hasSmsEnabled}
                  onChange={setDraftMessage}
                  onDismissReply={() => setReplyToMessage(null)}
                  onSubmit={() => sendSms.mutate()}
                />
              ) : selectedOption?.id === Channel.VOICE ? (
                <div className="flex items-center gap-3 py-2">
                  {person.default_phone && (
                    <PhoneNumber
                      phone={{ number: person.default_phone, default: true, kind: 'mobile' }}
                      onCall={handleCall}
                      size="base"
                      showNumberInLabel
                      showLink={false}
                      fullWidthOnMobile
                    />
                  )}
                </div>
              ) : (
                <EmailComposer
                  key={emailComposerKey}
                  recipientEmail={person.default_email ?? ''}
                  subject={emailSubject}
                  body={draftMessage}
                  isLoading={sendEmail.isPending}
                  replyToMessage={replyToMessage}
                  onSubjectChange={setEmailSubject}
                  onBodyChange={setDraftMessage}
                  onSend={(draftId, attachments) => sendEmail.mutate({ draftId, attachments })}
                  onPrepareAttachment={(file, draftId) =>
                    messageRepository.prepareAttachment({ personId: person.id, file, draftId })
                  }
                  onRemoveAttachment={(draftId, attachmentId) =>
                    messageRepository.removeAttachment({
                      draftId,
                      attachmentId,
                      personId: person.id
                    })
                  }
                  onDiscard={handleDiscard}
                  onDismissReply={handleDismissEmailReply}
                  onDirtyChange={setIsEmailComposerDirty}
                />
              )}
            </div>

            <div className="flex-shrink-0 bg-gray-100 pb-3">
              <MessageFilterBar
                filters={messageFilters}
                messages={messages}
                onFiltersChange={handleFiltersChange}
              />
            </div>

            {/* Message list */}
            <div className="overflow-y-auto max-h-[calc(100vh-20rem)] px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
              {isPendingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner label="Loading Messages..." />
                </div>
              ) : !messages.length &&
                !pendingMessages.filter(
                  (pm) => !messageFilters.channel || pm.channel === messageFilters.channel
                ).length ? (
                <div className="flex items-center justify-center py-12">
                  <Heading
                    text={
                      messageFilters.channel
                        ? `No ${
                            {
                              [Channel.SMS]: 'SMS messages',
                              [Channel.EMAIL]: 'email conversations',
                              [Channel.VOICE]: 'calls',
                              [Channel.CHAT]: 'chat messages'
                            }[messageFilters.channel] ?? messageFilters.channel
                          } found for ${formatPersonName({ person })}`
                        : `No messages found for ${formatPersonName({ person })}`
                    }
                    value={3}
                  />
                </div>
              ) : (
                <ul className="space-y-1 px-2" role="list">
                  {pendingMessages
                    .filter(
                      (pm) => !messageFilters.channel || pm.channel === messageFilters.channel
                    )
                    .map((pm) => (
                      <li key={pm.id}>
                        {pm.channel === Channel.EMAIL ? (
                          <EmailMessage message={pm} timezone={person.time_zone} isPending />
                        ) : (
                          <MessageBubble message={pm} timezone={person.time_zone} isPending />
                        )}
                      </li>
                    ))}
                  {messages.map((message) => {
                    const isTargetMessage = message.id === targetMessageId;
                    return (
                      <li
                        key={message.id}
                        ref={isTargetMessage ? targetMessageRef : undefined}
                        className={isTargetMessage ? 'ring-2 ring-blue-400 rounded-lg' : undefined}>
                        {message.channel === Channel.VOICE ? (
                          <VoiceMessage message={message} timezone={person.time_zone} />
                        ) : message.channel === Channel.EMAIL ? (
                          <EmailMessage
                            message={message}
                            timezone={person.time_zone}
                            onReply={handleReply}
                            onDownload={handleAttachmentDownload}
                          />
                        ) : (
                          <MessageBubble
                            message={message}
                            timezone={person.time_zone}
                            onReply={message.channel === Channel.SMS ? handleReply : undefined}
                            onDownload={handleAttachmentDownload}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              <div ref={sentinelRef} className="flex justify-center py-4">
                {isFetchingNextPage && <Spinner label="Loading more..." />}
              </div>
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default PersonMessaging;
